import React, { useState, useEffect } from 'react';
import { FireIcon } from '@heroicons/react/24/solid';
import api from '../services/api';

export default function StreakCounter() {
  const [streak, setStreak] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCelebration, setShowCelebration] = useState(false);

  useEffect(() => {
    fetchStreak();
    
    // Poll every 5 minutes to keep streak updated
    const interval = setInterval(fetchStreak, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, []);

  const fetchStreak = async () => {
    try {
      const response = await api.get('/auth/streak');
      setStreak(response.data);
      
      // Show celebration for milestones
      if (response.data.current_streak > 0 && isMilestone(response.data.current_streak)) {
        setShowCelebration(true);
        setTimeout(() => setShowCelebration(false), 3000);
      }
    } catch (error) {
      console.error('Failed to fetch streak:', error);
    } finally {
      setLoading(false);
    }
  };

  const isMilestone = (streak) => {
    const milestones = [7, 14, 30, 60, 90, 100, 180, 365];
    return milestones.includes(streak);
  };

  if (loading || !streak) {
    return null;
  }

  const currentStreak = streak.is_active ? streak.current_streak : 0;
  const daysUntilMilestone = streak.next_milestone ? streak.next_milestone - currentStreak : null;

  return (
    <div className="relative">
      {/* Main Streak Display */}
      <div className="flex items-center space-x-2 bg-gradient-to-r from-orange-50 to-red-50 px-4 py-2 rounded-lg border border-orange-200">
        <div className={`transition-transform duration-300 ${currentStreak > 0 ? 'scale-110' : 'scale-100'}`}>
          <FireIcon 
            className={`h-6 w-6 ${
              currentStreak > 0 ? 'text-orange-500' : 'text-gray-400'
            } ${showCelebration ? 'animate-bounce' : ''}`}
          />
        </div>
        
        <div>
          <div className="flex items-baseline space-x-1">
            <span className="text-2xl font-bold text-gray-900">
              {currentStreak}
            </span>
            <span className="text-sm text-gray-600">
              {currentStreak === 1 ? 'day' : 'days'}
            </span>
          </div>
          
          {currentStreak > 0 && (
            <p className="text-xs text-gray-500">
              Current streak
            </p>
          )}
          
          {currentStreak === 0 && (
            <p className="text-xs text-gray-500">
              Start your streak today!
            </p>
          )}
        </div>

        {/* Record Badge */}
        {streak.longest_streak > 0 && (
          <div className="ml-2 pl-2 border-l border-orange-200">
            <div className="text-xs text-gray-500">Best</div>
            <div className="text-sm font-semibold text-orange-600">
              {streak.longest_streak}
            </div>
          </div>
        )}
      </div>

      {/* Next Milestone Indicator */}
      {currentStreak > 0 && daysUntilMilestone && (
        <div className="mt-2 text-xs text-center text-gray-600">
          <span className="inline-flex items-center px-2 py-1 rounded-full bg-orange-100 text-orange-700">
            🎯 {daysUntilMilestone} {daysUntilMilestone === 1 ? 'day' : 'days'} until {streak.next_milestone}-day milestone
          </span>
        </div>
      )}

      {/* Celebration Overlay */}
      {showCelebration && (
        <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
          <div className="text-4xl animate-ping">🎉</div>
        </div>
      )}

      {/* Milestone Achievement Toast */}
      {showCelebration && (
        <div className="absolute -top-16 left-1/2 transform -translate-x-1/2 bg-gradient-to-r from-orange-500 to-red-500 text-white px-4 py-2 rounded-lg shadow-lg animate-bounce z-50">
          <div className="text-center">
            <div className="text-2xl mb-1">🔥</div>
            <div className="font-bold">{currentStreak}-Day Streak!</div>
            <div className="text-xs opacity-90">You're on fire!</div>
          </div>
        </div>
      )}
    </div>
  );
}
